import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types'; // Import PropTypes for props validation
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import './styles.scss';
import { useFringeMutation } from 'utils/api/mutations';
import { useQueryClient } from '@tanstack/react-query';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import CreateGroups from 'components/CreateGroups';
import { components } from 'react-select';
import { useGroupFringeQuery } from 'utils/api/queries';
import { toast } from 'react-toastify'; // <-- import toast here
import { toastStyle } from 'utils/helper'; // optional: your custom style
import { useLocation } from 'react-router-dom';

const CreateFringe = props => {
  const [groupOptions, setGroupOptions] = useState([]); // State for groups
  const createFringeMutation = useFringeMutation.useCreateFringeMutation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';

  const {
    showCreateGroupsModal,
    setshowCreateGroupsModal,
    activeBudget,
    hasEditAccess,
  } = useHomeScreenContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { data: budget_details, refetch } =
    useGroupFringeQuery.useGroupFringeDetailsQuery({
      filters: { instance_type: 'budget', active_id: activeBudget },
      enabled: true,
    });

  useEffect(() => {
    if (budget_details) {
      const formattedGroups =
        budget_details?.data?.data?.map(group => ({
          label: group.name,
          value: group.id,
        })) || [];
      setGroupOptions(formattedGroups);
    }
    setValue('fringe_type', 'percentage');
  }, [budget_details, setValue]);
  const selectedGroups = watch('group') || [];
  const selectedGroupValues = selectedGroups.map(g => g.value);

  const filteredGroupOptions = useMemo(() => {
    return groupOptions.filter(
      option => !selectedGroupValues.includes(option.value)
    );
  }, [groupOptions, selectedGroupValues]);

  const onSubmit = data => {
    // Build the payload
    const payload = {
      name: data.name,
      description: data.description,
      abbreviation: data.abbreviation,
      display_name: data.display_name,
      type: data.fringe_type,
      value: data.value,
      base_cap: data.base_cap,
      cap_on_total: data.cap_on_total,
      group: data.group?.map(group => group.value) || [],
      instance_type: instanceType,
      budget_id: activeBudget,
    };

    createFringeMutation.mutate(payload, {
      onSuccess: res => {
        // Invalidate every place that reads the fringe definitions or
        // anywhere a fringe might appear in the budget so the new fringe shows
        // up on the Fringes listing page, the inline dropdown, and any totals
        // derived from these queries — without requiring a manual reload.
        queryClient.invalidateQueries(['get-fringe-details']);
        queryClient.invalidateQueries(['get-group-fringe-detail']);
        props?.closeFolder();
        props.onFringeCreated?.(payload, res);
        reset();
      },
      onError: error => {
        // Default message if nothing more specific is found
        let message =
          'An error occurred while creating the fringe. Please try again.';

        if (error?.response?.data) {
          const responseData = error.response.data;

          // Handling abbreviation-specific error
          if (responseData.abbreviation) {
            message = responseData.abbreviation;
          }
          // Handling general API errors
          else if (responseData.non_field_errors?.length) {
            message = responseData.non_field_errors.join(', ');
          }
          // Handle when API returns a plain string message
          else if (typeof responseData === 'string') {
            message = responseData;
          }
        }

        toast.error(message, toastStyle);
        console.error('Error creating fringe:', error);
      },
    });
  };

  const handleGroupCreated = newGroup => {
    const newGroupOption = {
      value: newGroup.id,
      label: newGroup.name,
    };
    setGroupOptions(prev => [...prev, newGroupOption]);
    const currentSelected = watch('group') || [];
    const updatedSelected = [...currentSelected, newGroupOption];
    setValue('group', updatedSelected);
    setshowCreateGroupsModal(false);
  };

  const CustomMenuList = ({ children, ...rest }) => {
    return (
      <components.MenuList {...rest}>
        {children}
        <div
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
          }}
          onClick={() => setshowCreateGroupsModal(true)}
        >
          + Add Group
        </div>
      </components.MenuList>
    );
  };

  CustomMenuList.propTypes = {
    children: PropTypes.node.isRequired,
  };

  return (
    <>
      <Modal
        onHide={props?.closeFolder}
        show={props?.showCreateFringeModal}
        size="md"
        className="create-budget"
        bodyClass="create-budget__body"
        centered
      >
        <h1 className="create-budget__body__header">Add New Fringe</h1>
        <Form className="overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            label="Full Fringe Name"
            type="text"
            error={errors?.name?.message}
            placeholder="Please enter a name"
            {...register('name', {
              required: 'Fringe name is required',
              validate: value =>
                value.trim() !== '' || 'Fringe name cannot be just spaces',
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <FormInput
            label="Fringe Display Name"
            type="text"
            error={errors?.display_name?.message}
            placeholder="Please enter a short name"
            {...register('display_name', {
              required: 'Fringe display name is required',
              validate: value =>
                value.trim() !== '' ||
                'Fringe display name cannot be just spaces',
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <FormInput
            label="Fringe Description"
            type="textarea"
            error={errors?.description?.message}
            placeholder="Please provide the description"
            {...register('description')}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <FormInput
            label="Fringe Letter Abbreviation"
            type="text"
            error={errors?.abbreviation?.message}
            placeholder="Please enter fringe abbreviation"
            {...register('abbreviation', {
              required: 'Fringe abbreviation is required',
              validate: value =>
                value.trim() !== '' ||
                'Fringe abbreviation cannot be just spaces',
              maxLength: {
                value: 5,
                message: 'Abbreviation cannot exceed 5 letters',
              },
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <div className="toggle-button-fringes">
            <label className="Type-button">Type</label>
            <div className="toggle-button-fringe">
              <Button
                label="Percentage"
                className={`toggle-button-one ${
                  watch('fringe_type') === 'percentage' ? 'active' : ''
                }`}
                onClick={() => setValue('fringe_type', 'percentage')}
                variant={
                  watch('fringe_type') === 'percentage'
                    ? 'primary'
                    : 'secondary'
                }
                isDisabled={instanceType === 'budget' && !hasEditAccess()}
              />
              <Button
                label="Flat Fee"
                className={`toggle-button-one ${
                  watch('fringe_type') === 'flat fee' ? 'active' : ''
                }`}
                onClick={() => setValue('fringe_type', 'flat fee')}
                variant={
                  watch('fringe_type') === 'flat fee' ? 'primary' : 'secondary'
                }
                isDisabled={instanceType === 'budget' && !hasEditAccess()}
              />
            </div>
            {errors?.fringe_type && (
              <p className="error-message">{errors.fringe_type.message}</p>
            )}
          </div>
          <FormInput
            label="Value"
            type="text"
            error={errors?.value?.message}
            placeholder="Please enter a value"
            {...register('value', {
              required: 'Value is required',
              pattern: {
                value:
                  /^(?!.*[a-zA-Z])(?=.*\d)[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/,
                message:
                  'Only numbers and special characters are allowed (no letters)',
              },
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <FormInput
            label="Base Cap"
            type="text"
            error={errors?.base_cap?.message}
            placeholder="Enter Base Cap"
            {...register('base_cap', {
              pattern: {
                value:
                  /^(?!.*[a-zA-Z])(?=.*\d)[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/,
                message:
                  'Only numbers and special characters are allowed (no letters)',
              },
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <FormInput
            label="Cap on Total"
            type="text"
            error={errors?.cap_on_total?.message}
            placeholder="Enter Cap on Total"
            {...register('cap_on_total', {
              pattern: {
                value:
                  /^(?!.*[a-zA-Z])(?=.*\d)[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/,
                message:
                  'Only numbers and special characters are allowed (no letters)',
              },
            })}
            disabled={instanceType === 'budget' && !hasEditAccess()}
          />
          <div className="group-select-container">
            <FormSelect
              label="Group(s)"
              placeholder="Please select a Group"
              options={filteredGroupOptions} // Only real groups
              error={errors?.group?.message}
              name="group"
              control={control}
              className="mb-5"
              isMulti={true}
              menuPlacement="top"
              menuPortalTarget={document.body}
              components={{ MenuList: CustomMenuList }}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({
                  ...base,
                  Height: '200px',
                  overflowY: 'auto',
                }),
                menuList: base => ({
                  ...base,
                  maxHeight: '200px',
                  overflowY: 'auto',
                }),
                control: base => ({
                  ...base,
                  minHeight: '40px',
                }),
              }}
              isDisabled={instanceType === 'budget' && !hasEditAccess()}
            />
          </div>

          <div className="create-budget__body__action">
            <Button
              label="Cancel"
              type="reset"
              className="create-budget__action__cancel"
              variant="red-outlined"
              onClick={props?.closeFolder}
            />
            <Button
              label="Create"
              className="create-budget__action__confirm"
              type="submit"
              isDisabled={instanceType === 'budget' && !hasEditAccess()}
            />
          </div>
        </Form>
      </Modal>
      {showCreateGroupsModal && (
        <CreateGroups
          closeFolder={() => {
            setshowCreateGroupsModal(false);
            refetch();
          }}
          showCreateGroupsModal={showCreateGroupsModal}
          refetch={refetch}
          onGroupCreated={handleGroupCreated} // Pass the callback
        />
      )}
    </>
  );
};

CreateFringe.propTypes = {
  closeFolder: PropTypes.func.isRequired,
  showCreateFringeModal: PropTypes.bool.isRequired,
  onFringeCreated: PropTypes.func,
};
CreateFringe.defaultProps = {
  onFringeCreated: null,
};

export default CreateFringe;
